import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { ApiService } from 'app/shared/apis/api.service';
import { SocketService } from './socket.service';
import {
    AccessApprovalRequestModel,
    CheckCompanyAccessResult,
    CheckCompanyAccessStatus,
} from 'app/models/access-approval-request.model';

export interface AccessApprovalDecidedEvent {
    request_id: number;
    user_id: number;
    company_id: number;
    status: 'APPROVED' | 'REJECTED';
    decided_by_user_id: number | null;
    reason: string | null;
}

export interface AccessApprovalCreatedEvent {
    request_id: number;
    user_id: number;
    user_name: string | null;
    user_profile_type: string | null;
    company_id: number;
    requested_at: string;
}

/**
 * Aprovação de entrada por médico remoto: gate na "entrada na empresa"
 * (`POST kpis/acesso`) para usuários sem alocação de turno aberta. Estado
 * bloqueante consumido pelo `AccessApprovalGuard`; fila/decisão consumidas
 * pelo sininho do médico regulador (`is_regulator`). Sem polling: fila e
 * estado atualizam só por push (`access_approval_created` /
 * `access_approval_decided`), por `docs/realtime.md`.
 */
@Injectable({ providedIn: 'root' })
export class AccessApprovalService {
    private readonly endpoint = 'access-approval-requests';

    /** Último resultado conhecido da checagem de entrada (para a tela de espera). */
    readonly state$ = new BehaviorSubject<CheckCompanyAccessResult | null>(null);

    private currentUserId: number | null = null;

    constructor(private readonly api: ApiService, private readonly socketService: SocketService) {
        ApiService.userCurrent.subscribe((user) => {
            this.currentUserId = user?.user_id ?? null;
        });
        this.watchOwnDecision();
    }

    /** Chamada única na entrada da empresa — cria/consulta o pedido do dia e devolve o estado atual. */
    checkCompanyAccess(company_id: number): Observable<CheckCompanyAccessResult> {
        return this.api.postSub<CheckCompanyAccessResult>('kpis/acesso', { company_id }).pipe(
            tap((result) => this.state$.next(result)),
        );
    }

    listPending(company_id: number): Observable<AccessApprovalRequestModel[]> {
        return this.api
            .getSub<AccessApprovalRequestModel[]>(`${this.endpoint}/pending`, { company_id })
            .pipe(map((items) => (items || []).map((item) => new AccessApprovalRequestModel(item))));
    }

    /**
     * Única decisão manual que existe. Não existe "aprovar" — a entrada só
     * libera de verdade quando o médico remoto aloca a pessoa (o pedido some
     * sozinho da fila via `access_approval_decided` quando isso acontece).
     */
    reject(access_approval_request_id: number, reason?: string): Observable<AccessApprovalRequestModel> {
        return this.api
            .putSub<AccessApprovalRequestModel>(`${this.endpoint}/${access_approval_request_id}/reject`, { reason })
            .pipe(map((item) => new AccessApprovalRequestModel(item)));
    }

    /** Push ao médico remoto: novo pedido pendente na fila da empresa atual. */
    onCreated(): Observable<AccessApprovalCreatedEvent> {
        return this.socketService.fromEvent<AccessApprovalCreatedEvent>('access_approval_created');
    }

    /** Push tanto para quem aguardava quanto para os demais médicos remotos online. */
    onDecided(): Observable<AccessApprovalDecidedEvent> {
        return this.socketService.fromEvent<AccessApprovalDecidedEvent>('access_approval_decided');
    }

    /** Atualiza `state$` quando a decisão pushed é sobre o próprio usuário aguardando. */
    private watchOwnDecision(): void {
        this.onDecided().subscribe((event) => {
            if (this.currentUserId && event.user_id === this.currentUserId) {
                const status: CheckCompanyAccessStatus = event.status === 'APPROVED' ? 'ALLOWED' : 'REJECTED';
                this.state$.next({ ok: status === 'ALLOWED', status, request_id: event.request_id, reason: event.reason });
            }
        });
    }
}
